// The API serializes the BatchValidationStatus enum as a string ("NotValidated" /
// "Validated" / "Discrepancy"), but the raw docgen/Cosmos representation is the underlying
// integer (0 / 1 / 2). Accept both so the dashboard can't mis-render if the numeric form
// ever reaches it.
//   NotValidated (0) = pending — created by the nightly cycle, not yet validated
//   Validated    (1) = reconciled — every piece confirmed by the vendor
//   Discrepancy  (2) = processed, but a piece-count mismatch was found
export type BatchValidationStatus = 'NotValidated' | 'Validated' | 'Discrepancy' | 0 | 1 | 2;

// Normalize either representation to a boolean. 'Validated' / 1 => validated.
export function isBatchValidated(status: BatchValidationStatus | null | undefined): boolean {
	return status === 'Validated' || status === 1;
}

// True when validation has NOT run yet (NotValidated / 0 / missing). Distinct from
// isBatchValidated: Discrepancy also means validation ran (just with a mismatch), so it is
// NOT pending. Mailed / Not Mailed determinations are only meaningful once this is false.
export function isBatchValidationPending(status: BatchValidationStatus | null | undefined): boolean {
	return !(status === 'Validated' || status === 1 || status === 'Discrepancy' || status === 2);
}

// Three-way display descriptor for the validation status chip. Pending is neutral (it just
// means "not yet validated"), Reconciled is success, Discrepancy is a warning.
export function batchValidationDisplay(
	status: BatchValidationStatus | null | undefined,
): { label: string; color: string } {
	if (status === 'Validated' || status === 1) return { label: 'Reconciled', color: 'success' };
	if (status === 'Discrepancy' || status === 2) return { label: 'Discrepancy', color: 'warning' };
	return { label: 'Pending', color: 'grey' };
}

export interface BatchSummary {
	id: string;
	dateStarted: Date;
	dateFinished: Date | null;
	totalPoliciesProcessed: number;
	hasErrors: boolean;
	policySummaries: BatchPolicySummary[];
	batchFiles: BatchFileSummary[];
	validationStatus: BatchValidationStatus;
	confirmedCount: number;
}

export interface BatchFileSummary {
	batchName: string;
	filePath: string | null;
	policies: BatchPolicySummary[];
}

export interface BatchPolicySummary {
	policyNumber: string;
	amendmentNumber: string;
	transactionType: string;
	policyHolderName: string | null;
	agencyCode: string;
	agencyName: string | null;
	documentCreated: boolean | null;
	cloudArchived: boolean | null;
	locallyArchived: boolean | null;
	trackerDocumentId: string;
	excludedByTracker: boolean;
	excludedByDocProd: boolean;
	trackerIsCompleted: boolean;
	trackerDocumentRulesNotExecuted: boolean;
	trackerHasIncompletePolicyForms: boolean;
	trackerHasIncompleteInteractiveForms: boolean;
	docProdWipIncomplete: boolean;
	docProdError: boolean;
	successful: boolean;
	startTime: Date | null;
	endTime: Date | null;
	totalPages: number;
	isPaperless: boolean;
	formCodes: string[];
	interestEntries: BatchInterestEntry[];
	interestPieceCount: number;
	policyHolderAddressLines: string[];
	errorMessages: string[];
	printConfirmation: PrintConfirmation[];
}

export interface PrintConfirmation {
	vendor: string | null;
	printDateTime: string | null;
	sequenceNumber: string | null;
	mailedTo: string | null;
}

export interface BatchInterestEntry {
	interestCode: string;
	interestName: string;
}

export class GetBatchSummariesRequest {
	limit?: number | null;
	fromDate?: Date | null;
	toDate?: Date | null;

	constructor(init?: Partial<GetBatchSummariesRequest>) {
		Object.assign(this, init);
	}
}
